import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Order } from '../../../../../../components/Table/types';
import DoDisturbOnTwoToneIcon from '@mui/icons-material/DoDisturbOnTwoTone';
import HorizontalRuleTwoToneIcon from '@mui/icons-material/HorizontalRuleTwoTone';
import RemoveRedEyeTwoToneIcon from '@mui/icons-material/RemoveRedEyeTwoTone';
import {
  Box,
  CircularProgress, FormControlLabel, Snackbar, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip,
  TextField, Button
} from '@mui/material';
import styles from '../../../../../users/components/UsersTableComponent/UserTableComponent.module.scss';
import { visuallyHidden } from '@mui/utils';
import { getComparator, stableSort } from '../../../../../../components/Table/data';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { deleteGroup, getAvailableGroups } from '../../../../../../api/settings';
import MuiAlert, { AlertColor } from '@mui/material/Alert';
import FilterAltOffTwoToneIcon from '@mui/icons-material/FilterAltOffTwoTone';

const columns = [
  { id: 'name', label: 'Name' },
  { id: 'description', label: 'Description' },
  { id: 'image', label: 'Image' },
  { id: 'leagues', label: 'Leagues' },
  { id: 'isPaid', label: 'Is paid' },
  { id: 'isDeleted', label: 'Is deleted' },
  { id: 'level', label: 'Level' },
  { id: 'price', label: 'Price' },
  { id: 'createdBy', label: 'Owner' }
];

export const GroupTableComponent = (
    { isOpenNewGroupDialog, setSelectedGroup }: 
    { isOpenNewGroupDialog: boolean; setSelectedGroup(group: any): void }
  ) => {
  const [groupsList, setGroupsList] = useState<any>([]);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('name');
  const tableEl = useRef<any>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [distanceBottom, setDistanceBottom] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState<{isOpen: boolean, alertType: AlertColor, message: string}>({
    isOpen: false,
    alertType: 'success',
    message: ''
  });
  const [groupType, setGroupType] = useState<'PUBLIC' | 'PRIVATE' | string | null>('PRIVATE');
  const [searchQueryByName, setSearchQueryByName] = useState('');
  const [filterByPaid, setFilterByPaid] = useState(false);
  const [filterByDeleted, setFilterByDeleted] = useState(false);
  
  useEffect(() => {
    (async () => {
      const _groupList = await getAvailableGroups({ type: groupType, isDeleted: filterByDeleted });
      if (Array.isArray(_groupList)) {
        setGroupsList(_groupList);
      }
    })();
  }, [isOpenNewGroupDialog]);
  
  const loadMore = useCallback(() => {
    const loadItems = async () => {
      await new Promise(resolve =>
        setTimeout(() => {
          // getMoreUsers();
          setLoading(false);
          resolve(true);
        }, 2000)
      )
    };
    
    setLoading(true);
    loadItems();
  }, [groupsList.length]);
  
  const scrollListener = useCallback(() => {
    if (tableEl && tableEl.current) {
      let bottom = tableEl?.current?.scrollHeight - tableEl?.current?.clientHeight
      
      if (!distanceBottom) {
        setDistanceBottom(Math.round(bottom * 0.2))
      }
      
      if (tableEl.current.scrollTop > bottom - distanceBottom && hasMore && !loading) {
        loadMore()
      }
    }
  }, [hasMore, loadMore, loading, distanceBottom, tableEl]);
  
  useEffect(() => {
    if (tableEl && tableEl.current) {
      tableEl.current.addEventListener('scroll', scrollListener, false);
      
      return () => tableEl?.current?.removeEventListener('scroll', scrollListener, false);
    }
  }, [tableEl]);
  
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: any) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  
  const handleRowItem = (valueType: string, value: any) => {
    switch (valueType) {
      case 'isDeleted':
        return <span />;
      case 'isPaid': {
        return value ? <CheckCircleTwoToneIcon color="success"/> : <DoDisturbOnTwoToneIcon color="error"/>
      }
      case 'description': {
        return <span className="max-w-[200px]">{value}</span>
      }
      case 'level': {
        return <span>{value?.name}</span>
      }
      case 'image': {
        return <img src={value?.url} alt={value?.url} className="w-[40px] h-auto rounded-md" />
      }
      case 'leagues': {
        return (
          <div className="max-w-[200px]">
            {value?.map((item: any, index: number) =>
              <span className="block" key={index.toString()}>{item.name}{value.length !== index + 1 && <span>, </span>}</span>
            )}
          </div>
        )
      }
      default: return <span>{typeof value === 'string' ? value : <HorizontalRuleTwoToneIcon color="disabled" />}</span>;
    }
  };
  
  const handleTooltipPreview = (valueType: string, value: any) => {
    switch (valueType) {
      case 'isDeleted': {
        return <span>{value ? 'Deleted' : 'Undeleted'}</span>
      }
      case 'isPaid': {
        return <span>{value ? 'Paid' : 'Unpaid'}</span>
      }
      case 'level': {
        return <span>{value?.name}</span>
      }
      case 'leagues': {
        return (
          <div className="max-w-[400px]">
            {value.map((item: any, index: number) =>
              <span key={index.toString()}>{item.name}{value.length !== index + 1 && <span>, </span>}</span>
            )}
          </div>
        )
      }
      default: return <span>{typeof value === 'string' ? value : <DoDisturbOnTwoToneIcon color="warning"/>}</span>;
    }
  };
  
  const handleDeleteGroup = async (group: any) => {
    try {
      await deleteGroup(group.id)
      const updatedGroupsList = groupsList.filter((item: any) => item.id !== group.id);
      setGroupsList(updatedGroupsList);
      setOpenSnackbar({ isOpen: true, message: 'Group has been successfully deleted', alertType: 'success' });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: 'Something went wrong, unable to delete the group, please try again later',
        alertType: 'error'
      });
    }
  };
  
  useEffect(() => {
    (async () => {
      await handleGetGroupsByFilter();
    })();
  }, [filterByPaid, filterByDeleted, groupType]);
  
  const handleGetGroupsByFilter = async () => {
    const filterConfig = {
      ...(filterByPaid && { isPaid: filterByPaid }),
      isDeleted: filterByDeleted,
      ...(searchQueryByName.length && { name: searchQueryByName }),
      ...(groupType && { type: groupType})
    }

    const _groupList = await getAvailableGroups(filterConfig);
    if (Array.isArray(_groupList)) {
      setGroupsList(_groupList);
    }
  };
  
  const handleSearchGroupsByName = async () => {
    await handleGetGroupsByFilter();
  };
  
  const handleRemoveFilters = async () => {
    setFilterByDeleted(false);
    setFilterByPaid(false);
    setSearchQueryByName('');
  };
  
  return (
    <div className="w-full h-full">
      <div className="mb-10">
        <div className="flex items-center">
          <span className="block text-xl italic opacity-50 font-bold">Filters</span>
          {(filterByPaid || filterByDeleted || searchQueryByName.length > 0) && (
            <Button
              color="error"
              variant="outlined"
              className="!ml-5"
              onClick={handleRemoveFilters}
            >
              Remove filtering
              <FilterAltOffTwoToneIcon className="ml-2" color="error" />
            </Button>
          )}
        </div>
        <div className="flex content-center p-5 mt-3 border-y border-[#a1a1a1]">
          <FormControlLabel
            labelPlacement="top"
            className="block w-max text-xl"
            control={
              <Switch
                defaultChecked={false}
                checked={filterByPaid}
                onChange={(event) => setFilterByPaid(event.target.checked)}
              />
            }
            label="Only paid groups"
          />
          <FormControlLabel
            labelPlacement="top"
            className="block w-max text-xl !ml-5"
            control={
              <Switch
                defaultChecked={false}
                checked={filterByDeleted}
                onChange={(event) => setFilterByDeleted(event.target.checked)}
              />
            }
            label="Only deleted groups"
          />
          <div className="ml-14 flex items-center h-max">
            <TextField
              color="info"
              label="Search groups by name"
              value={searchQueryByName}
              onChange={(event) => setSearchQueryByName(event.target.value)}
              className="w-[300px]"
            />
            <Button
              className="!ml-4 h-max"
              variant="contained"
              onClick={handleSearchGroupsByName}
            >
              Search
            </Button>
          </div>
        </div>
      </div>
      <TableContainer sx={{ maxHeight: "60vh", width: '100%', position: 'relative', marginBottom: '20px' }} ref={tableEl}>
        <Table stickyHeader aria-labelledby="tableTitle" size="small" className={styles.table}>
          <TableHead>
            <TableRow>
              {groupsList.length > 0 && columns.map((item: any, index: number) => (
                <TableCell
                  key={`groupsList-row-header-${index}`}
                  style={{ minWidth: 50 }}
                  sortDirection={orderBy === item.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === item.id}
                    direction={orderBy === item.id ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, item.id)}
                    className="capitalize font-bold"
                  >
                    {item.label}
                    {orderBy === item.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
              {groupsList.length > 0 && (
                <TableCell key="action" align="center">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {!!groupsList?.length && (
              <>
                {stableSort(groupsList as any, getComparator(order, orderBy))?.map((row: any, index: number) => {
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={`groupsList-row-${index}`}
                      className={styles.tableRow}
                      onClick={() => setSelectedGroup(row)}
                    >
                      {columns.map((column, colIndex) => {
                        return (
                          <TableCell key={`groupsList-row-${index}-col-${colIndex}`} className={styles.rowItemContainer}>
                            <Tooltip title={handleTooltipPreview(column.id, row[column.id])} placement="top">
                              {handleRowItem(column.id, row[column.id])}
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                      <TableCell key="action" align="center">
                        {!row.isDeleted ? (
                          <Tooltip title={`Delete group "${row['name']}"`} placement="top">
                            <DeleteTwoToneIcon color="error" onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteGroup(row);
                            }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title={`View "${row['name']}"`} placement="top">
                            <RemoveRedEyeTwoToneIcon color="info" />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
        <div className={styles.loadingContainer}>
          {loading && <CircularProgress className={styles.loading} />}
        </div>
      </TableContainer>
      <Snackbar
        open={openSnackbar.isOpen}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
      >
        <MuiAlert
          onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
          severity={openSnackbar.alertType}
          sx={{ width: '100%' }}
        >
          {openSnackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  )
}